import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft, Users, IndianRupee, MessageSquare, Activity, Loader2, Search,
  X, Coins, Mail, KeyRound, Send,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
};

const StatCard = ({ icon: Icon, label, value, sub }) => (
  <div className="rounded-2xl border border-white/10 bg-[#141415] p-5">
    <div className="flex items-center gap-2 text-zinc-400 text-sm mb-2">
      <Icon className="w-4 h-4 text-[#00FF66]" /> {label}
    </div>
    <div className="font-heading font-extrabold text-3xl text-white">{value}</div>
    {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
  </div>
);

export default function Admin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null); // null => broadcast
  const [email, setEmail] = useState({ subject: "", body: "" });
  const [sending, setSending] = useState(false);
  const [campaign, setCampaign] = useState(null);
  const [runningCampaign, setRunningCampaign] = useState(false);

  useEffect(() => {
    if (user && user.role !== "admin") { navigate("/app"); return; }
    load();
    // eslint-disable-next-line
  }, [user]);

  const load = async () => {
    try {
      const [s, u, c] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/campaigns/winback"),
      ]);
      setStats(s.data);
      setUsers(u.data);
      setCampaign(c.data);
    } catch (e) {
      toast.error("Failed to load admin data");
    }
  };

  const runWinback = async () => {
    setRunningCampaign(true);
    try {
      const { data } = await api.post("/admin/campaigns/winback");
      toast.success(`Win-back campaign sent to ${data.emailed} inactive user(s)`);
      const c = await api.get("/admin/campaigns/winback");
      setCampaign(c.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setRunningCampaign(false);
    }
  };

  const filtered = useMemo(() => {
    if (!users) return [];
    const t = q.trim().toLowerCase();
    if (!t) return users;
    return users.filter((u) => (u.email || "").toLowerCase().includes(t) || (u.name || "").toLowerCase().includes(t));
  }, [users, q]);

  const openDetail = async (uid) => {
    setDetailLoading(true);
    setDetail({ id: uid });
    try {
      const { data } = await api.get(`/admin/users/${uid}`);
      setDetail(data);
    } catch {
      toast.error("Failed to load user");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const adjustCredits = async (uid, amount) => {
    try {
      const { data } = await api.post(`/admin/users/${uid}/credits`, { amount });
      toast.success(`Credits updated → ${data.credits}`);
      setDetail((d) => (d ? { ...d, credits: data.credits } : d));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const resetPassword = async (uid) => {
    const pw = window.prompt("Set a new password for this user (min 6 chars):");
    if (!pw) return;
    try {
      await api.post(`/admin/users/${uid}/password`, { password: pw });
      toast.success("Password updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const sendEmail = async () => {
    if (!email.subject.trim() || !email.body.trim()) { toast.error("Subject and body required"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/admin/email", {
        user_ids: emailTarget ? [emailTarget] : [],
        subject: email.subject,
        body: email.body,
      });
      toast.success(`Email queued to ${data.queued} user(s)`);
      setEmailOpen(false);
      setEmail({ subject: "", body: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const statCards = [
    { icon: Users, label: "Total users", value: (stats?.total_users ?? 0).toLocaleString() },
    { icon: IndianRupee, label: "Revenue", value: `₹${(stats?.total_revenue_inr ?? 0).toLocaleString()}`, sub: `${(stats?.total_credits_sold ?? 0).toLocaleString()} credits sold` },
    { icon: MessageSquare, label: "Total messages", value: (stats?.total_messages ?? 0).toLocaleString() },
    { icon: Activity, label: "Active today", value: (stats?.active_today ?? 0).toLocaleString() },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="admin-dashboard">
      <header className="border-b border-white/10 nig-glass sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <button data-testid="admin-back" onClick={() => navigate("/app")} className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to app
          </button>
          <span className="font-heading font-extrabold text-lg tracking-tight">Admin Dashboard</span>
          <Button
            data-testid="admin-broadcast-btn"
            onClick={() => { setEmailTarget(null); setEmailOpen(true); }}
            className="bg-[#00FF66] hover:bg-[#00CC52] text-black font-semibold rounded-lg h-9"
          >
            <Mail className="w-4 h-4 mr-1.5" /> Email all
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {!users ? (
          <div className="flex items-center justify-center py-32"><Loader2 className="w-6 h-6 text-[#00FF66] animate-spin" /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {statCards.map((s, i) => <StatCard key={i} {...s} />)}
            </div>

            <div className="rounded-2xl border border-[#00FF66]/20 bg-gradient-to-br from-[#101a12] to-[#141415] p-5 mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-testid="winback-panel">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#00FF66]/15 flex items-center justify-center shrink-0">
                  <Send className="w-5 h-5 text-[#00FF66]" />
                </div>
                <div>
                  <p className="text-white font-semibold">Win-back campaign</p>
                  <p className="text-sm text-zinc-400">
                    <span className="text-[#00FF66] font-semibold">{campaign?.eligible_now ?? 0}</span> inactive user(s) eligible now
                    · runs automatically {campaign?.scheduled || "daily"}
                    {campaign && !campaign.email_enabled && <span className="text-amber-400"> · email disabled</span>}
                  </p>
                </div>
              </div>
              <Button
                data-testid="run-winback-btn"
                onClick={runWinback}
                disabled={runningCampaign || (campaign?.eligible_now ?? 0) === 0}
                className="bg-[#00FF66] hover:bg-[#00CC52] text-black font-semibold rounded-lg shrink-0"
              >
                {runningCampaign ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send win-back emails now"}
              </Button>
            </div>

            <div className="flex items-center gap-3 mb-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  data-testid="admin-search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by email or name…"
                  className="pl-9 bg-[#141415] border-white/10 text-white h-10"
                />
              </div>
              <span className="text-sm text-zinc-500">{filtered.length} users</span>
            </div>

            <div className="rounded-2xl border border-white/10 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#141415] text-zinc-400">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium">User</th>
                      <th className="text-right px-4 py-3 font-medium">Credits</th>
                      <th className="text-right px-4 py-3 font-medium">Purchased</th>
                      <th className="text-right px-4 py-3 font-medium">Revenue</th>
                      <th className="text-right px-4 py-3 font-medium">Messages</th>
                      <th className="text-right px-4 py-3 font-medium">Chats</th>
                      <th className="text-left px-4 py-3 font-medium">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((u) => (
                      <tr
                        key={u.id}
                        data-testid={`admin-user-row-${u.id}`}
                        onClick={() => openDetail(u.id)}
                        className="border-t border-white/5 hover:bg-white/[0.03] cursor-pointer transition-colors"
                      >
                        <td className="px-4 py-3">
                          <div className="text-white flex items-center gap-2">
                            {u.email}
                            {u.role === "admin" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#00FF66]/20 text-[#00FF66] font-semibold">ADMIN</span>}
                          </div>
                          <div className="text-xs text-zinc-500">{u.name}</div>
                        </td>
                        <td className="px-4 py-3 text-right text-white">{u.unlimited ? "∞" : u.credits}</td>
                        <td className="px-4 py-3 text-right text-zinc-300">{u.purchased_credits}</td>
                        <td className="px-4 py-3 text-right text-zinc-300">₹{u.revenue_inr}</td>
                        <td className="px-4 py-3 text-right text-zinc-300">{u.messages}</td>
                        <td className="px-4 py-3 text-right text-zinc-300">{u.conversations}</td>
                        <td className="px-4 py-3 text-zinc-500">{fmt(u.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </main>

      {/* User detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-[#0F0F10] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="admin-user-detail">
          {detailLoading || !detail?.email ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-[#00FF66] animate-spin" /></div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="text-white">{detail.email}</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="rounded-xl border border-white/10 bg-[#141415] p-3">
                  <div className="text-xs text-zinc-500">Credits</div>
                  <div className="text-xl font-bold">{detail.unlimited ? "∞" : detail.credits}</div>
                </div>
                <div className="rounded-xl border border-white/10 bg-[#141415] p-3">
                  <div className="text-xs text-zinc-500">Streak</div>
                  <div className="text-xl font-bold">{detail.streak}🔥</div>
                </div>
                <div className="rounded-xl border border-white/10 bg-[#141415] p-3">
                  <div className="text-xs text-zinc-500">Joined</div>
                  <div className="text-sm font-medium mt-1">{fmt(detail.created_at)}</div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-5">
                <Button data-testid="admin-add-credits" size="sm" onClick={() => adjustCredits(detail.id, 100)} className="bg-[#00FF66] hover:bg-[#00CC52] text-black font-semibold">
                  <Coins className="w-4 h-4 mr-1" /> +100 credits
                </Button>
                <Button size="sm" variant="outline" onClick={() => adjustCredits(detail.id, -50)} className="bg-transparent border-white/15 text-white hover:bg-white/5">
                  −50 credits
                </Button>
                <Button data-testid="admin-reset-pw" size="sm" variant="outline" onClick={() => resetPassword(detail.id)} className="bg-transparent border-white/15 text-white hover:bg-white/5">
                  <KeyRound className="w-4 h-4 mr-1" /> Reset password
                </Button>
                <Button data-testid="admin-email-user" size="sm" variant="outline" onClick={() => { setEmailTarget(detail.id); setEmailOpen(true); }} className="bg-transparent border-white/15 text-white hover:bg-white/5">
                  <Mail className="w-4 h-4 mr-1" /> Email user
                </Button>
              </div>

              <Section title={`Purchases (${detail.purchases?.length || 0})`}>
                {(detail.purchases || []).length === 0 ? <Empty /> : detail.purchases.map((p, i) => (
                  <Row key={i} left={`${p.kind} · ${p.credits} credits`} right={`₹${p.amount}`} sub={fmt(p.created_at)} />
                ))}
              </Section>

              <Section title={`Recent activity (${detail.usage?.length || 0})`}>
                {(detail.usage || []).length === 0 ? <Empty /> : detail.usage.slice(0, 30).map((x, i) => (
                  <Row key={i} left={x.title || "Message"} right={`${x.cost} cr`} sub={`${x.model} · ${fmt(x.created_at)}`} />
                ))}
              </Section>

              <Section title={`Conversations (${detail.conversations?.length || 0})`}>
                {(detail.conversations || []).length === 0 ? <Empty /> : detail.conversations.slice(0, 30).map((c) => (
                  <Row key={c.id} left={c.title} right="" sub={fmt(c.updated_at)} />
                ))}
              </Section>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Email dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="bg-[#0F0F10] border-white/10 text-white" data-testid="admin-email-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">{emailTarget ? "Email user" : "Email all users"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              data-testid="admin-email-subject"
              value={email.subject}
              onChange={(e) => setEmail({ ...email, subject: e.target.value })}
              placeholder="Subject"
              className="bg-[#141415] border-white/10 text-white"
            />
            <Textarea
              data-testid="admin-email-body"
              value={email.body}
              onChange={(e) => setEmail({ ...email, body: e.target.value })}
              placeholder="Write your message… (basic HTML allowed)"
              className="bg-[#141415] border-white/10 text-white min-h-[160px]"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)} className="bg-transparent border-white/15 text-white hover:bg-white/5">Cancel</Button>
            <Button data-testid="admin-email-send" onClick={sendEmail} disabled={sending} className="bg-[#00FF66] hover:bg-[#00CC52] text-black font-semibold">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" /> Send</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="mb-5">
    <h3 className="text-sm font-semibold text-zinc-300 mb-2">{title}</h3>
    <div className="rounded-xl border border-white/10 divide-y divide-white/5 overflow-hidden">{children}</div>
  </div>
);
const Row = ({ left, right, sub }) => (
  <div className="flex items-center justify-between px-3 py-2.5">
    <div className="min-w-0">
      <div className="text-sm text-white truncate">{left}</div>
      {sub && <div className="text-xs text-zinc-500 truncate">{sub}</div>}
    </div>
    {right && <div className="text-sm text-[#00FF66] shrink-0 ml-3">{right}</div>}
  </div>
);
const Empty = () => <div className="px-3 py-4 text-sm text-zinc-500">Nothing yet.</div>;
